import { useCallback, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Select from '@mui/material/Select';
import ListSubheader from '@mui/material/ListSubheader';
import AddIcon from '@mui/icons-material/AddRounded';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { usePopupZIndex } from '@components/Modal/popupLayer';
import { CssMenuItem, CssNoOptions, CssMenuItemCreatable } from '../Select';
import { Checkbox } from '../Checkbox';
import { CssInputLabel, CssFormControl, CssOutlinedInput } from '../Input';

const CssMenuItemCheckbox = styled(CssMenuItem, {
    shouldForwardProp: prop => prop !== 'groupBased' && prop !== 'isSelected',
})(({ disabled, groupBased, isSelected }) => {
    return {
        '&.MuiMenuItem-root': {
            width: '100%',
            padding: groupBased ? '0 18px 0 45px' : '0 18px 0 9px',
            display: 'flex',
            alignItems: 'center',
            minHeight: '32px',
            ...((disabled || isSelected) && {
                backgroundColor: '#E4EAEB',
            }),
            '&>*:first-of-type': {
                marginRight: 9,
                ...(disabled && {
                    color: 'var(--text-primary)',
                }),
            },
        },
    };
});

const CssCheckbox = styled(Checkbox)({
    padding: '7px 9px 7px 9px',
    '& .MuiSvgIcon-root': {
        fontSize: '1.125rem',
    },
});

const CssSelect = styled(Select)({
    '.MuiSelect-icon': {
        color: 'rgba(0, 0, 0, 0.88)',
    },
    '& .MuiSelect-icon.Mui-disabled': {
        color: 'var(--text-tertiary)',
    },
    '.MuiCheckbox-root': {
        '&.Mui-checked': {
            color: 'var(--primary-main)',
        },
    },
});

const OptionAll = styled('div')({
    position: 'absolute',
    content: '""',
    height: '1px',
    width: '100%',
    background: 'var(--divider)',
    left: 0,
    bottom: '-8px',
});

const MultiselectErrorWrapper = styled('div')(({ error }) => {
    return {
        position: 'relative',
        height: 'fit-content',
        '& .MuiInputLabel-root': {
            color: error && '#B00020 !important',
        },
    };
});

const ErrText = styled('p')({
    position: 'absolute',
    bottom: '-16px',
    left: 11,
    color: 'var(--error-main)',
    fontFamily: 'Rubik,sans-serif',
    fontSize: '12px',
    lineHeight: '14px',
    letterSpacing: '0.4px',
});

const CssListSubheader = styled(ListSubheader)({
    lineHeight: '32px !important',
    fontFamily: 'Rubik',
    fontWeight: '500 !important',
    fontSize: '10px !important',
    letterSpacing: '1.5px !important',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
});

export const MultiSelect = ({
    className,
    options: propsOptions = [],
    label,
    value = [],
    id,
    required,
    countable,
    nameBased = false,
    InputProps = {},
    style,
    onChange,
    error,
    groupBased = false,
    allOption = true,
    creatable = false,
    creatableText = '',
    creatableClick = () => {},
    ...rest
}) => {
    const { t } = useTranslation();
    const popupZIndex = usePopupZIndex();
    const isMobile = useMediaQuery('@media (max-width: 1024px)');
    const [open, setOpen] = useState(false);
    const hasOneGroup = groupBased && propsOptions.some(option => option.children) && propsOptions.length === 1;

    const allOptions = useMemo(() => {
        if (groupBased) {
            return propsOptions.reduce((acc, group) => [...acc, ...group.children], []);
        }
        return propsOptions;
    }, [propsOptions]);

    const selectableOptions = useMemo(
        () => (groupBased ? allOptions : allOptions.filter(option => !option.disabled || value.includes(option.value))),
        [groupBased, allOptions, value],
    );

    const options = allOptions.length
        ? [{ value: 'all', label: t('common.all') }, ...(groupBased ? propsOptions : allOptions)]
        : [];

    const checkGroupForSelection = useCallback(
        group => {
            const groupChildren = propsOptions.find(option => option.group === group)?.children || [];
            const groupValues = groupChildren.map(option => option.value);
            const selectedGroupValues = value
                .filter(selected => groupValues.includes(selected.value))
                .map(selected => selected.value);

            return {
                all: groupValues.length === selectedGroupValues.length && groupValues.length > 0,
                some: selectedGroupValues.length > 0 && groupValues.length !== selectedGroupValues.length,
            };
        },
        [propsOptions, value],
    );

    const selectGroup = useCallback(
        group => {
            const groupOption = propsOptions.find(option => option.group === group);
            if (!groupOption) return;

            const groupChildren = groupOption.children || [];
            const groupValues = groupChildren.map(option => option);

            const { all } = checkGroupForSelection(group);

            let newValue = [...value];

            if (all) {
                newValue = newValue.filter(selected => !groupValues.some(option => option.value === selected.value));
            } else {
                newValue = newValue.filter(selected => !groupValues.some(option => option.value === selected.value));
                newValue = [...newValue, ...groupValues];
            }
            onChange(newValue);
        },
        [propsOptions, value, onChange, checkGroupForSelection],
    );

    const renderOptions = useCallback(() => {
        const allOptionsDisabled = groupBased
            ? allOptions.every(option => option.disabled)
            : options.slice(1).every(option => option.disabled);

        const renderAllOption = () => (
            <CssMenuItemCheckbox
                key="all"
                value="all"
                disabled={allOptionsDisabled}
                sx={{ marginBottom: '16px', overflow: 'visible !important' }}
            >
                <CssCheckbox
                    indeterminate={!!value.length && selectableOptions.length !== value.length}
                    checked={!!selectableOptions.length && selectableOptions.length === value.length}
                />
                <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} variant="body2">
                    {t('common.all')}
                </Typography>
                <OptionAll />
            </CssMenuItemCheckbox>
        );

        const renderMenuItem = option => {
            const isSelected = value?.some(selected =>
                groupBased ? selected.value === option.value : selected === option.value,
            );
            return (
                <CssMenuItemCheckbox
                    key={option.value}
                    value={groupBased ? option : option.value}
                    disabled={option?.disabled}
                    groupBased={groupBased && !hasOneGroup}
                    isSelected={isSelected}
                >
                    <CssCheckbox checked={isSelected} />
                    <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} variant="body2">
                        {option.label}
                    </Typography>
                </CssMenuItemCheckbox>
            );
        };

        const allItems = allOption ? [renderAllOption()] : [];

        if (!groupBased) {
            return [...allItems, ...options.slice(1).map(renderMenuItem)];
        }

        const groupedItems = options.slice(1).map((group, index) => {
            const items = group.children.map(renderMenuItem);

            if (hasOneGroup) {
                return items;
            } else {
                const { all, some } = checkGroupForSelection(group.group);

                return [
                    <div
                        key={`group-header-${index}`}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 9px',
                        }}
                    >
                        <CssCheckbox
                            checked={all}
                            indeterminate={some}
                            onChange={() => selectGroup(group.group)}
                            sx={{ marginRight: '9px' }}
                        />
                        <CssListSubheader
                            disableSticky
                            component="div"
                            sx={{ lineHeight: '32px !important', padding: 0 }}
                        >
                            {group.group}
                        </CssListSubheader>
                    </div>,
                    items,
                ];
            }
        });

        return [...allItems, ...groupedItems.flat()];
    }, [options, value, hasOneGroup, allOptions, selectableOptions, allOption, checkGroupForSelection, selectGroup, t]);

    const handleChange = event => {
        const isCreatable = event.target.value.includes('create-new');
        if (isCreatable) {
            return;
        }
        const selectedValues = event.target.value;
        let result = selectedValues.filter(item => item !== 'all');

        if (selectedValues.includes('all')) {
            if (result.length < selectableOptions.length) {
                result = groupBased ? allOptions : selectableOptions.map(item => item.value);
            } else {
                result = [];
            }
        } else if (groupBased) {
            const currentValue = value || [];
            const addedItem = result.find(item => !currentValue.some(v => v.value === item.value));

            if (addedItem) {
                result = [...currentValue, addedItem];
            } else {
                const seen = new Set();
                let removedValue;
                for (const item of result) {
                    if (seen.has(item.value)) {
                        removedValue = item.value;
                        break;
                    }
                    seen.add(item.value);
                }
                if (removedValue === undefined) {
                    removedValue = currentValue.find(v => !result.some(item => item.value === v.value))?.value;
                }
                result = currentValue.filter(v => v.value !== removedValue);
            }
        }
        onChange(result);
    };

    return (
        <MultiselectErrorWrapper style={style} error={!!error}>
            <CssFormControl fullWidth required={required} className={className}>
                <CssInputLabel>{label}</CssInputLabel>
                <CssSelect
                    id={id}
                    value={value}
                    multiple
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    onChange={handleChange}
                    input={<CssOutlinedInput label={label} {...InputProps} />}
                    renderValue={selectedValues => {
                        const selectedOptions = selectedValues.map(sV =>
                            allOptions.find(({ value }) => (groupBased ? sV.value : sV) === value),
                        );

                        if (allOption && selectedOptions.length === allOptions.length) {
                            return t('common.all');
                        }
                        const countPrefix = `${countable ? `(${selectedOptions.length}) ` : ''}`;
                        return (
                            countPrefix +
                            selectedOptions.map(option => (nameBased ? option?.name : option?.label)).join(', ')
                        );
                    }}
                    MenuProps={{
                        autoFocus: false,
                        style: { zIndex: popupZIndex },
                        slotProps: {
                            ...(isMobile
                                ? {
                                      list: {
                                          style: {
                                              maxWidth: 'initial',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                          },
                                      },
                                  }
                                : {}),
                            paper: {
                                sx: {
                                    '& .MuiList-root': {
                                        paddingBottom: creatable ? 0 : '8px',
                                    },
                                },
                                style: {
                                    maxHeight: isMobile ? '35vh' : '55vh',
                                    maxWidth: isMobile ? '210px' : 'none',
                                },
                            },
                        },
                    }}
                    error={!!error}
                    {...rest}
                >
                    {options.length ? renderOptions() : <CssNoOptions>{t('common.noOptions')}</CssNoOptions>}
                    {creatable ? (
                        <CssMenuItemCreatable
                            onClick={e => {
                                setOpen(false);
                                creatableClick(e);
                            }}
                            value="create-new"
                        >
                            <AddIcon />
                            <Typography sx={{ color: 'var(--primary-main)' }} variant="body2">
                                {creatableText}
                            </Typography>
                        </CssMenuItemCreatable>
                    ) : null}
                </CssSelect>
            </CssFormControl>
            {error ? <ErrText>{error ? t(error.message) : null}</ErrText> : null}
        </MultiselectErrorWrapper>
    );
};
